import React from "react";
import { Link } from "react-router-dom";
import PracticeLayout from "../../components/PracticeLayout";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  MousePointer, FileEdit, MousePointer2, Layers, Loader, MonitorSmartphone,
  Boxes, Rocket, ArrowRight, Terminal,
} from "lucide-react";
import useDocumentTitle from "../../hooks/useDocumentTitle";

const LEVELS = [
  {
    slug: "basic", icon: MousePointer, tone: "from-blue-500 to-blue-700",
    title: "Level 1 · Basic",
    tag: "Beginner",
    subtitle: "Text · Button · Link · Input · Checkbox · Radio · Dropdown",
    blurb: "Every automation script starts here. Get comfortable with the seven building-block controls before anything else.",
  },
  {
    slug: "forms", icon: FileEdit, tone: "from-emerald-500 to-emerald-700",
    title: "Level 2 · Forms",
    tag: "Beginner",
    subtitle: "Textarea · Date Picker · File Upload · Slider · Autocomplete · Validation",
    blurb: "Learn to wait on debounced inputs, upload files, drive sliders and assert form-level validation errors.",
  },
  {
    slug: "mouse-keyboard", icon: MousePointer2, tone: "from-amber-500 to-orange-600",
    title: "Level 3 · Mouse & Keyboard",
    tag: "Intermediate",
    subtitle: "Hover · Double-click · Right-click · Drag & Drop · Keyboard · Scrolling",
    blurb: "Master pointer gestures, contextual menus, drag operations and keyboard shortcuts for realistic user flows.",
  },
  {
    slug: "complex", icon: Layers, tone: "from-violet-500 to-fuchsia-600",
    title: "Level 4 · Complex Components",
    tag: "Intermediate",
    subtitle: "Tabs · Accordion · Modal · Tooltip · Pagination · Carousel · Stepper",
    blurb: "The composite widgets you see on every product page. Great for practising synchronisation and ARIA-based locators.",
  },
  {
    slug: "dynamic", icon: Loader, tone: "from-sky-500 to-cyan-600",
    title: "Level 5 · Dynamic UI",
    tag: "Intermediate",
    subtitle: "Dynamic Elements · AJAX · Loading · Infinite Scroll · Conditional Fields · Dynamic Tables",
    blurb: "Where flaky tests are born. Practise smart waits, retries and idempotent locators against elements that appear over time.",
  },
  {
    slug: "browser", icon: MonitorSmartphone, tone: "from-rose-500 to-red-600",
    title: "Level 6 · Browser Handling",
    tag: "Advanced",
    subtitle: "Alerts · Popups · Multiple Tabs · New Windows · Permissions · Downloads",
    blurb: "Anything that leaves the DOM: native alerts, tab-switching, download validation and permission prompts.",
  },
  {
    slug: "advanced", icon: Boxes, tone: "from-slate-700 to-slate-900",
    title: "Level 7 · Advanced Automation",
    tag: "Advanced",
    subtitle: "Iframes · Shadow DOM · Virtualized Tables · Custom Web Components · Authentication",
    blurb: "The tricky stuff. Encapsulated DOM, virtualised rows, custom elements and multiple auth strategies (cookie vs token).",
  },
];

export default function PracticeLanding() {
  useDocumentTitle("Practice — QaTestology");
  return (
    <PracticeLayout
      eyebrow="practice ground"
      title="Sharpen your automation, for free"
      subtitle="Seven hands-on levels for UI automation plus a full CRUD sandbox for API testing. Every element ships with a stable data-testid — write your scripts with confidence."
      seo={{
        title: "Automation Practice Site — 7 UI levels + API playground",
        description: "Free demo website for automation practice. Practise UI automation with Selenium, Playwright, Cypress and WebdriverIO plus API testing with a real CRUD sandbox.",
        keywords: "automation practice website, ui automation practice, api testing practice, selenium demo site, playwright practice site, cypress practice",
        image: "/og/og-default.svg",
      }}
    >
      <Card className="p-5 mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center border-blue-100 bg-blue-50/60">
        <span className="h-11 w-11 rounded-md bg-[#002FA7] text-white grid place-items-center flex-shrink-0">
          <Rocket className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-bold tracking-tight">
            Prefer to practice API testing?
          </div>
          <p className="text-sm text-zinc-700 mt-0.5">
            Every job, profile and application endpoint is exposed as a real REST API. Read/GET requests are open, write requests need a bearer token — perfect for learning auth headers.
          </p>
        </div>
        <Link
          to="/practice/api"
          data-testid="practice-goto-api-btn"
          className="tag-mono inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#002FA7] hover:bg-[#00227A] text-white text-sm font-semibold self-stretch sm:self-auto whitespace-nowrap"
        >
          <Terminal className="h-4 w-4" /> Open API Playground
        </Link>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        {LEVELS.map(({ slug, icon: Icon, tone, title, tag, subtitle, blurb }) => (
          <Link
            key={slug}
            to={`/practice/level/${slug}`}
            data-testid={`practice-card-${slug}`}
            className="group"
          >
            <Card className="p-5 h-full transition-all border-zinc-200 hover:border-[#002FA7] hover:shadow-md">
              <div className="flex items-start gap-3 mb-3">
                <span className={`h-11 w-11 rounded-md bg-gradient-to-br ${tone} text-white grid place-items-center flex-shrink-0`}>
                  <Icon className="h-5 w-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-display font-bold text-zinc-950 truncate">{title}</h3>
                    <Badge variant="outline" className="tag-mono text-[10px]">{tag}</Badge>
                  </div>
                  <div className="text-xs text-zinc-500 tag-mono mt-0.5 truncate">{subtitle}</div>
                </div>
              </div>
              <p className="text-sm text-zinc-700 leading-relaxed">{blurb}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#002FA7] group-hover:gap-2 transition-all">
                Start practising <ArrowRight className="h-4 w-4" />
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-10 text-xs tag-mono text-zinc-500 text-center">
        Made for testers, by testers. Free forever. Bookmark and share with your team.
      </div>
    </PracticeLayout>
  );
}
